/**
 * Receipt voucher hero — `prototype/sales/invoice-detail.html` ▸ `.doc` adapted.
 *
 * Markup mirrors the invoice/bill doc-paper conventions so it picks up
 * the shared chrome from receipt-detail.css (forked from invoice-detail.css):
 *   `.doc-bar` (toolbar) → `.doc-paper` containing
 *     `.doc-letterhead` (seller + marker)
 *     `.doc-meta-grid` (to + dates)
 *     `.doc-items` (money table — bank credited / TDS / settlement lines)
 *     `.doc-totals` (FX note + stacked rows)
 *     `.doc-foot` (notes + .pay)
 *
 * Numeric cells use `<td class="r">` / `<td class="r amount">`. Money
 * values render via `paiseToInrExact`. GST sub-lines indent under their
 * `parent_line_id` parent via a ↳ glyph (ADR-0019 parent_line_id).
 */
import { initials } from "../../../lib/initials.ts";
import { formatDate, paiseToInrExact } from "../../../lib/format.ts";

export interface ReceiptSettlementLine {
  id: string;
  parentLineId: string | null;
  kind: string;
  direction: "deduction" | "addition";
  label: string;
  amountPaise: string;
  currencyCode: string;
  foreignAmountMinor: string | null;
  fxRateToInrPaise: string | null;
}

export interface ReceiptVoucherProps {
  receipt: {
    id: string;
    receivedOn: string;
    amountPaise: string;
    tdsPaise: string;
    fxGainLossPaise: string;
    settlementRatePaise: string | null;
    tdsSectionSnapshot: string | null;
    mode: string;
    reference: string | null;
    allocations: Array<{ invoiceId: string; paise: string; allocatedOn: string }>;
    settlementLines: ReceiptSettlementLine[];
    voidsId: string | null;
  };
  customer: { name: string; tdsSection: string | null };
  invoice: {
    id: string;
    docNumber: string | null;
    currencyCode: string;
    foreignAmountMinor: string | null;
    fxRateToInrPaise: string | null;
    fxRateDate: string | null;
  } | null;
  company: {
    legalName: string;
    address: string | null;
    city: string | null;
    state: string | null;
    pincode: string | null;
    gstin: string | null;
    bankName: string | null;
    bankAccountNumber: string | null;
    bankIfsc: string | null;
  };
}

export function ReceiptVoucher({ receipt, customer, invoice, company }: ReceiptVoucherProps) {
  const rcpLabel = `RCP-${receipt.id.slice(0, 8)}`;
  const isForeign = invoice?.currencyCode !== "INR" && invoice?.currencyCode != null;
  const isVoid = !!receipt.voidsId;
  const buyerAddress = [company.address, company.city, company.state, company.pincode]
    .filter(Boolean)
    .join(", ");
  const appliedPaise = receipt.allocations.reduce((acc, a) => acc + BigInt(a.paise), 0n);

  // Split settlement lines: top-level (no parent) + children-by-parent.
  const childrenByParent = new Map<string, ReceiptSettlementLine[]>();
  const topLevel: ReceiptSettlementLine[] = [];
  for (const line of receipt.settlementLines) {
    if (line.parentLineId) {
      const arr = childrenByParent.get(line.parentLineId) ?? [];
      arr.push(line);
      childrenByParent.set(line.parentLineId, arr);
    } else {
      topLevel.push(line);
    }
  }

  return (
    <article className="doc" aria-label="Printable receipt voucher">
      <div className="doc-bar">
        <span className="filename">
          <strong>{rcpLabel}.pdf</strong> · receipt voucher
        </span>
      </div>
      <div className="doc-paper">
        {/* Letterhead — issuing tenant (we received the money) */}
        <header className="doc-letterhead">
          <div className="seller">
            <div className="logo" aria-hidden>
              {initials(company.legalName)}
            </div>
            <h2>{company.legalName}</h2>
            <div className="addr">
              {buyerAddress ? <strong>{buyerAddress}</strong> : null}
              {company.gstin ? (
                <>
                  <br />
                  GSTIN&nbsp;<span className="num">{company.gstin}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="marker">
            <div className="label">Receipt Voucher</div>
            <h1>{rcpLabel}</h1>
            <div className="num-line">
              <span className="k">Received</span>
              {formatDate(receipt.receivedOn)}
            </div>
            {isVoid ? (
              <div className="num-line">
                <span className="k" style={{ color: "var(--bad)" }}>
                  Void
                </span>
                <span style={{ color: "var(--bad)" }}>reversal row</span>
              </div>
            ) : null}
          </div>
        </header>

        {/* Received-from + receipt-details — `.doc-meta-grid` with `.to` + `.dates` */}
        <div className="doc-meta-grid">
          <div className="to">
            <div className="lbl">Received from</div>
            <div className="name">{customer.name}</div>
            <div className="addr">
              {customer.tdsSection ? (
                <span className="num">TDS · {customer.tdsSection}</span>
              ) : (
                <span className="num">No TDS section on file</span>
              )}
            </div>
          </div>
          <div className="dates">
            <div className="cell">
              <div className="k">Mode</div>
              <div className="v">{receipt.mode.toUpperCase()}</div>
            </div>
            <div className="cell">
              <div className="k">Reference</div>
              <div className="v">{receipt.reference || "—"}</div>
            </div>
            {invoice ? (
              <div className="cell">
                <div className="k">Against</div>
                <div className="v">{invoice.docNumber || "Draft"}</div>
                {isForeign ? (
                  <div className="sub">
                    {invoice.currencyCode}{" "}
                    {invoice.foreignAmountMinor
                      ? formatForeign(BigInt(invoice.foreignAmountMinor))
                      : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="cell">
              <div className="k">Received on</div>
              <div className="v">{formatDate(receipt.receivedOn)}</div>
            </div>
          </div>
        </div>

        {/* Money table — Bank credited / TDS / settlement lines */}
        <table className="doc-items" aria-label="Receipt money breakdown">
          <thead>
            <tr>
              <th>Description</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="desc">
                <strong>Bank credited</strong>
                <span className="sub">
                  {receipt.mode.toUpperCase()} · {receipt.reference || "no reference"}
                </span>
              </td>
              <td className="r amount">
                {withoutRupee(paiseToInrExact(BigInt(receipt.amountPaise)))}
              </td>
            </tr>
            {receipt.tdsPaise !== "0" ? (
              <tr>
                <td className="desc">
                  <strong>TDS deducted by customer</strong>
                  <span className="sub">
                    Section {receipt.tdsSectionSnapshot ?? "—"} · paid by customer to government
                  </span>
                </td>
                <td className="r amount">
                  {withoutRupee(paiseToInrExact(BigInt(receipt.tdsPaise)))}
                </td>
              </tr>
            ) : null}
            {topLevel.length > 0 ? (
              <>
                {topLevel.map((line) => (
                  <SettlementLineRows
                    key={line.id}
                    line={line}
                    childRows={childrenByParent.get(line.id) ?? []}
                  />
                ))}
              </>
            ) : null}
          </tbody>
        </table>

        {/* Totals — note (left) + stack (right) */}
        <div className="doc-totals">
          <div className="note">
            <strong style={{ color: "var(--ink-2)" }}>
              {isForeign ? "Foreign-currency receipt" : "Domestic receipt"}
            </strong>{" "}
            —{" "}
            {isForeign
              ? "INR amounts above are the realised settlement after foreign-bank deductions and customer-side TDS, if any. Reconciles against the INR bank-statement entry on the same date."
              : "Allocation against the invoice is recorded for the gross billed amount; TDS shown above is the customer-deducted portion, available against Form 26AS at quarter-end."}
          </div>
          <div className="stack">
            <div className="row">
              <span>Bank credited</span>
              <span className="v">
                {withoutRupee(paiseToInrExact(BigInt(receipt.amountPaise)))}
              </span>
            </div>
            {receipt.tdsPaise !== "0" ? (
              <div className="row">
                <span>TDS</span>
                <span className="v">{withoutRupee(paiseToInrExact(BigInt(receipt.tdsPaise)))}</span>
              </div>
            ) : null}
            {isForeign && receipt.fxGainLossPaise !== "0" ? (
              <div className="row">
                <span>FX {BigInt(receipt.fxGainLossPaise) > 0n ? "gain" : "loss"}</span>
                <span className="v">{formatSignedRupees(BigInt(receipt.fxGainLossPaise))}</span>
              </div>
            ) : null}
            <div className="row grand">
              <span>Applied to invoice</span>
              <span className="v">{paiseToInrExact(appliedPaise)}</span>
            </div>
            {isForeign && invoice?.fxRateToInrPaise ? (
              <div className="row">
                <span>Booked rate</span>
                <span className="v">
                  ₹{formatRate(BigInt(invoice.fxRateToInrPaise))}/{invoice.currencyCode}
                </span>
              </div>
            ) : null}
            {isForeign && receipt.settlementRatePaise ? (
              <div className="row">
                <span>Settled rate</span>
                <span className="v">
                  ₹{formatRate(BigInt(receipt.settlementRatePaise))}/{invoice!.currencyCode}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer — notes left, pay right */}
        <div className="doc-foot">
          <div>
            <strong style={{ color: "var(--ink-2)" }}>Acknowledged.</strong> Receipt entered to
            ledger on <span className="num">{formatDate(receipt.receivedOn)}</span>.
            {customer.tdsSection ? (
              <> TDS certificate available on Form 16A at quarter-end.</>
            ) : null}
          </div>
          <div className="pay">
            <span className="k">Received in</span> <strong>{company.legalName}</strong>
            <br />
            <span className="k">Bank</span> {company.bankName ?? "—"}
            <br />
            <span className="k">A/c</span> {company.bankAccountNumber ?? "—"}
            <br />
            <span className="k">IFSC</span> {company.bankIfsc ?? "—"}
          </div>
        </div>
      </div>
    </article>
  );
}

// ── Settlement-line rows (top-level + nested child via ↳) ────────

function SettlementLineRows({
  line,
  childRows,
}: {
  line: ReceiptSettlementLine;
  childRows: ReceiptSettlementLine[];
}) {
  const isAddition = line.direction === "addition";
  const sign = isAddition ? "+" : "−";
  return (
    <>
      <tr>
        <td className="desc">
          <strong>{line.label}</strong>
          <span className="sub">{humanKind(line.kind)}</span>
        </td>
        <td className="r amount">
          {sign}
          {withoutRupee(paiseToInrExact(BigInt(line.amountPaise)))}
        </td>
      </tr>
      {childRows.map((child) => (
        <tr key={child.id}>
          <td className="desc">
            <span style={{ color: "var(--muted)", marginRight: 8 }}>↳</span>
            {child.label}
            <span className="sub">{humanKind(child.kind)}</span>
          </td>
          <td className="r amount">
            {child.direction === "addition" ? "+" : "−"}
            {withoutRupee(paiseToInrExact(BigInt(child.amountPaise)))}
          </td>
        </tr>
      ))}
    </>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function humanKind(kind: string): string {
  return kind.replace(/_/g, " ");
}

function withoutRupee(value: string): string {
  return value.replace("₹", "").trim();
}

function formatForeign(minor: bigint): string {
  const major = Number(minor) / 100;
  return major.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatRate(paise: bigint): string {
  const rupees = Number(paise) / 100;
  return rupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function formatSignedRupees(paise: bigint): string {
  if (paise === 0n) return "0.00";
  const sign = paise < 0n ? "−" : "+";
  const abs = paise < 0n ? -paise : paise;
  return `${sign}${withoutRupee(paiseToInrExact(abs))}`;
}
